import { closeRoute, type CloseAction, type CloseRequest } from '../jobs/close';

import { changeGarmentState } from './bookingCommands';
import { apiFetch } from './client';
import type { GarmentJob, GarmentJobBatch } from './garmentJobs';

/**
 * The cleaning and alteration queue's writes both clients send — the website's queue and the
 * phone's — built once, on `bookingCommands.ts`'s terms.
 */

export interface DispatchGarmentJobsInput {
  jobIds: readonly number[];
  vendorId: number;
  /** Left null, the server takes the vendor's usual turnaround. */
  expectedBackOn: string | null;
  notes: string | null;
}

/** Hands garments to a vendor and issues the challan. */
export function dispatchGarmentJobs(input: DispatchGarmentJobsInput) {
  return apiFetch<GarmentJobBatch>('/api/garment-job/dispatch', { method: 'POST', body: input });
}

/**
 * Takes garments back in from whoever had them. `finish` is "back, and good": each is finished in
 * the same press and goes back on the rail. Without it they wait, back but not yet looked at.
 */
export function receiveGarmentJobs(jobIds: readonly number[], finish = false) {
  return apiFetch<GarmentJob[]>('/api/garment-job/receive', {
    method: 'POST',
    body: { jobIds, finish },
  });
}

/**
 * Back from the tailor, ready — for an alteration a booking owns, sent as the booking's own move
 * (`changeGarmentState` to `Ready`), never as a job close: that is what releases the tailor's block
 * and closes the job together, while the line stops reading "at the tailor".
 */
export function markReadyOnTheBooking(job: GarmentJob) {
  if (job.rentalOrderId === null || job.rentalOrderItemId === null) {
    throw new Error('This job is not on a booking.');
  }

  return changeGarmentState(job.rentalOrderId, job.rentalOrderItemId, { state: 'Ready' });
}

/** Finishes, sends round again, reports damage on, or moves the date of one job. */
export function closeGarmentJob(id: number, action: CloseAction, request: CloseRequest) {
  return apiFetch<GarmentJob>(`/api/garment-job/${id}/${closeRoute[action]}`, {
    method: 'POST',
    body: request,
  });
}
